import React, { useState } from "react";
import { Link } from "@components/Catalyst/link";
import { GatsbyImage, IGatsbyImageData, withArtDirection } from "gatsby-plugin-image";
import { Brand } from "@typed/categories";
import { ChevronLeftIcon } from "@heroicons/react/16/solid";
import clsx from "clsx";
import Footer from "../Footer";
import { GacMobileSidebar } from "../Navbar/Mobile/GacMobileSidebar";
import GacSASidebar from "../Navbar/Mobile/GacSASidebar";
import { GACMobileSidebarWrapper } from "../Navbar/GACMobileSidebarWrapper";
import MobileNavbar from "../Navbar/Mobile";
import { DesktopNavbar } from "../Navbar/Desktop";
import NavSearchDialog from "../Navbar/NavSearchDialog";

interface MainProps {
  useFullWidth?: boolean;
  children: React.ReactNode;
  title?: string;
  heroImage?: IGatsbyImageData | null;
  heroImageMobile?: IGatsbyImageData | null;
  heroImageAlt?: string;
  goBack?: { to: string; label: string } | { to: string; label: string }[];
  style?: React.CSSProperties;
  brand?: Brand;
}

const Main = ({ useFullWidth, children, title, heroImage, heroImageMobile, heroImageAlt, goBack, style, brand }: MainProps) => {
  const goBackArray = Array.isArray(goBack) ? goBack : [goBack];

  const brandCode = brand?.code ?? "gac";

  const [showSearch, setShowSearch] = useState(false);

  const [showMenuSidebar, setShowMenuSidebar] = useState(false);
  const [showSASidebar, setShowSASidebar] = useState(false);

  return (
    <div className="relative isolate flex min-h-svh flex-col">
      <DesktopNavbar brandCode={brandCode} handleSearch={() => setShowSearch(!showSearch)} />
      <MobileNavbar
        brandCode={brandCode}
        showSASidebarClicked={() => setShowSASidebar(!showSASidebar)}
        showMenuSidebarClicked={() => setShowMenuSidebar(!showMenuSidebar)}
      />

      <main
        className={clsx(
          "isolate mb-2 flex-1",
          !useFullWidth && "mx-auto my-4 flex w-[min(100%-2rem,1440px)] max-w-[1440px] flex-col gap-1 sm:w-[98vw]",
        )}
        style={style}
      >
        {heroImage && heroImageMobile && (
          <GatsbyImage
            image={withArtDirection(heroImage, [
              {
                media: "(max-width: 550px)",
                image: heroImageMobile,
              },
            ])}
            alt={heroImageAlt ?? ""}
            style={{ marginBottom: "0.5rem" }}
          />
        )}
        {title && (
          <div className="flex flex-col">
            {goBack &&
              goBackArray.map(
                (gB) =>
                  gB && (
                    <Link
                      key={gB.to}
                      className="inline-flex items-center gap-1 text-base/6 text-zinc-500 dark:text-zinc-400"
                      href={gB.to}
                      onClick={() => {
                        if (typeof window !== "undefined") (window as any)?.plausible?.("GoBack-Click", { props: { url: gB.to } });
                      }}
                    >
                      <ChevronLeftIcon className="size-5 fill-zinc-400" />
                      <p className="underline">{gB.label}</p>
                    </Link>
                  ),
              )}

            <h1 className="text-2xl">{title}</h1>
          </div>
        )}
        {children}
      </main>

      <GACMobileSidebarWrapper open={showMenuSidebar} close={() => setShowMenuSidebar(false)}>
        <GacMobileSidebar
          brand={brand}
          handleSearch={() => {
            setShowMenuSidebar(false);
            setShowSearch(true);
          }}
        />
      </GACMobileSidebarWrapper>

      <GACMobileSidebarWrapper open={showSASidebar} close={() => setShowSASidebar(false)}>
        <GacSASidebar />
      </GACMobileSidebarWrapper>

      <NavSearchDialog searchOpen={showSearch} setSearchOpen={setShowSearch} />

      <Footer />
    </div>
  );
};

export default Main;
